// ============================================
// SERVICE: HUMOR
// ============================================
// Funções para gerenciar estados de humor via API
// ============================================

import { API_CONFIG } from './staticDataConfig';

const baseURL = API_CONFIG.BASE_URL;

export interface Humor {
  id: number;
  valor: string;
  descricao?: string;
  categoria?: string;
  cor_indicativa?: string;
  ativo: boolean;
  ordem: number;
}

class HumorService {
  private getToken(): string | null {
    return sessionStorage.getItem('token');
  }

  private getHeaders(): HeadersInit {
    const token = this.getToken();
    return {
      'Content-Type': 'application/json',
      ...(token && { 'Authorization': `Bearer ${token}` })
    };
  }

  // Buscar todos os estados de humor (ativos ou incluindo inativos)
  async obterTodos(incluirInativos: boolean = false): Promise<Humor[]> {
    try {
      const url = incluirInativos 
        ? `${baseURL}/humor?incluirInativos=true`
        : `${baseURL}/humor`;
      
      const response = await fetch(url, {
        method: 'GET',
        headers: this.getHeaders()
      });

      if (!response.ok) {
        throw new Error(`Erro ao buscar humor: ${response.statusText}`);
      }

      const data = await response.json();
      // console.log(`[HumorService] Estados de humor obtidos: ${data.humor?.length || 0} (incluirInativos: ${incluirInativos})`);
      return data.humor || [];
    } catch (error) {
      console.error('[HumorService] Erro ao obter humor:', error);
      throw error;
    }
  }

  // Adicionar novo estado de humor
  async adicionar(humor: {
    valor: string;
    descricao?: string;
    categoria?: string;
    cor_indicativa?: string;
    ordem?: number;
  }): Promise<Humor> {
    try {
      const response = await fetch(`${baseURL}/humor`, {
        method: 'POST',
        headers: this.getHeaders(),
        body: JSON.stringify(humor)
      });

      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.mensagem || 'Erro ao adicionar humor');
      }

      const data = await response.json();
      return data.humor;
    } catch (error) {
      console.error('[HumorService] Erro ao adicionar humor:', error);
      throw error;
    }
  }

  // Atualizar estado de humor existente
  async atualizar(id: number, humor: {
    valor?: string;
    descricao?: string;
    categoria?: string;
    cor_indicativa?: string;
    ordem?: number;
    ativo?: boolean;
  }): Promise<Humor> {
    try {
      const response = await fetch(`${baseURL}/humor/${id}`, {
        method: 'PUT',
        headers: this.getHeaders(),
        body: JSON.stringify(humor)
      });

      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.mensagem || 'Erro ao atualizar humor');
      }

      const data = await response.json();
      return data.humor;
    } catch (error) {
      console.error('[HumorService] Erro ao atualizar humor:', error);
      throw error;
    }
  }

  // Excluir estado de humor (proteção inteligente)
  async excluir(id: number, forcarDelete: boolean = false): Promise<any> {
    try {
      const url = forcarDelete 
        ? `${baseURL}/humor/${id}?forcarDelete=true`
        : `${baseURL}/humor/${id}`;
        
      const response = await fetch(url, {
        method: 'DELETE',
        headers: this.getHeaders()
      });

      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.mensagem || 'Erro ao excluir humor');
      }
      
      return await response.json();
    } catch (error) {
      console.error('[HumorService] Erro ao excluir humor:', error);
      throw error;
    }
  }
}

export const humorService = new HumorService();
